import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/hooks/useAuth';
import { useCurrentTimeEntry, useTimeAttendanceMutations } from '@/hooks/timeAttendance';
import { useGeolocationSettings } from '@/hooks/useGeolocationSettings';
import { Clock, ClockIcon, MapPin, CheckCircle2, AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { format } from 'date-fns';
import { useState } from 'react';
import { captureGeolocation } from '@/utils/geolocation';
import { LocationConfirmationDialog } from '@/components/timeAttendance/LocationConfirmationDialog';
import { LocationPermissionDialog } from '@/components/timeAttendance/LocationPermissionDialog';
import { GeofenceErrorDialog } from '@/components/timeAttendance/GeofenceErrorDialog';
import type { GeoLocation, GeoErrorStatus } from '@/services/timeAttendance/types';
import type { GeofenceErrorDetails } from '@/hooks/timeAttendance/useTimeAttendanceMutations';

const TimeAttendance = () => {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const [showLocationConfirm, setShowLocationConfirm] = useState(false);
  const [showPermissionDialog, setShowPermissionDialog] = useState(false);
  const [geofenceError, setGeofenceError] = useState<GeofenceErrorDetails | null>(null);
  const [pendingAction, setPendingAction] = useState<'in' | 'out' | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);

  // React Query hooks
  const { data: timeEntryData, isLoading, error } = useCurrentTimeEntry(profile?.id);
  const mutations = useTimeAttendanceMutations(profile?.id || '', setGeofenceError);
  const { settings, isLoading: settingsLoading } = useGeolocationSettings();

  const geoEnabled = settings?.geo_enabled || false;
  const geoStrictMode = settings?.geo_strict_mode || false;

  const formatTime = (dateString: string) => {
    return format(new Date(dateString), 'h:mm a');
  };

  const formatDateTime = (dateString: string) => {
    return format(new Date(dateString), 'MMM d, yyyy h:mm a');
  };

  const formatDuration = (clockIn: string, clockOut: string) => {
    const start = new Date(clockIn);
    const end = new Date(clockOut);
    const diffMs = end.getTime() - start.getTime();
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const handleLocationCapture = async () => {
    setIsCapturing(true);
    console.log('[TimeAttendance] Starting geolocation capture...');
    
    const result = await captureGeolocation();
    setIsCapturing(false);

    console.log('[TimeAttendance] Capture result:', {
      success: result.success,
      errorCode: result.errorCode,
      durationMs: result.durationMs,
      wasRetry: result.wasRetry,
      hasLocation: !!result.location,
    });

    if (!result.success) {
      if (result.errorCode === 'PERMISSION_DENIED') {
        setShowPermissionDialog(true);
        return null;
      }
      
      if (geoStrictMode) {
        setShowPermissionDialog(true);
        return null;
      }
      
      // Non-strict mode: proceed without location but inform user
      console.warn('[TimeAttendance] Proceeding without location (non-strict mode):', result.error);
      
      // Show informational toast so admin/user knows location was not captured
      import('sonner').then(({ toast }) => {
        toast.warning('Location unavailable', {
          description: `${result.error}. Clock action will proceed without location data.`,
          duration: 5000,
        });
      });
      
      return null;
    }

    return result.location;
  };

  const executeClockAction = async (location: GeoLocation | null) => {
    if (!profile) return;

    if (pendingAction === 'in') {
      mutations.clockIn.mutate({
        profile_id: profile.id,
        company_id: profile.company_id,
        ip_address_in: null,
        geo_location: location || undefined,
      });
    } else if (pendingAction === 'out' && timeEntryData?.entry?.id) {
      mutations.clockOut.mutate({
        entry_id: timeEntryData.entry.id,
        ip_address_out: null,
        geo_location: location || undefined,
      });
    }

    setPendingAction(null);
  };

  const getLocationConfirmPreference = (userId: string): boolean => {
    const storageKey = `location-confirm-preference-${userId}`;
    return localStorage.getItem(storageKey) === 'true';
  };

  const setLocationConfirmPreference = (userId: string, dontShow: boolean) => {
    const storageKey = `location-confirm-preference-${userId}`;
    if (dontShow) {
      localStorage.setItem(storageKey, 'true');
    } else {
      localStorage.removeItem(storageKey);
    }
  };

  const handleClockIn = async () => {
    if (!profile) return;
    
    if (geoEnabled) {
      const shouldSkipDialog = getLocationConfirmPreference(profile.id);
      
      if (shouldSkipDialog) {
        // Skip dialog and directly capture location
        setPendingAction('in');
        const location = await handleLocationCapture();
        if (location || !geoStrictMode) {
          await executeClockAction(location);
        }
        setPendingAction(null);
      } else {
        // Show dialog
        setPendingAction('in');
        setShowLocationConfirm(true);
      }
    } else {
      mutations.clockIn.mutate({
        profile_id: profile.id,
        company_id: profile.company_id,
        ip_address_in: null,
      });
    }
  };

  const handleClockOut = async () => {
    if (!timeEntryData?.entry?.id || !profile) return;
    
    if (geoEnabled) {
      const shouldSkipDialog = getLocationConfirmPreference(profile.id);
      
      if (shouldSkipDialog) {
        // Skip dialog and directly capture location
        setPendingAction('out');
        const location = await handleLocationCapture();
        if (location || !geoStrictMode) {
          await executeClockAction(location);
        }
        setPendingAction(null);
      } else {
        // Show dialog
        setPendingAction('out');
        setShowLocationConfirm(true);
      }
    } else {
      mutations.clockOut.mutate({
        entry_id: timeEntryData.entry.id,
        ip_address_out: null,
      });
    }
  };

  const handleLocationConfirm = async (dontShowAgain: boolean) => {
    if (profile && dontShowAgain) {
      setLocationConfirmPreference(profile.id, true);
    }
    
    setShowLocationConfirm(false);
    const location = await handleLocationCapture();
    
    if (location || !geoStrictMode) {
      await executeClockAction(location);
    }
  };

  const handlePermissionRetry = async () => {
    setShowPermissionDialog(false);
    const location = await handleLocationCapture();
    
    if (location || !geoStrictMode) {
      await executeClockAction(location);
    }
  };

  const getGeoStatusBadge = () => {
    if (!timeEntryData?.entry?.geo_status) return null;

    const status = timeEntryData.entry.geo_status;
    const isWithinRadius = status === 'within_radius';
    const isOutside = status === 'outside_radius';

    if (!isWithinRadius && !isOutside) return null;

    return (
      <Badge 
        variant={isWithinRadius ? 'default' : 'destructive'}
        className="flex items-center gap-1"
      >
        {isWithinRadius ? (
          <>
            <CheckCircle2 className="h-3 w-3" />
            Within radius
          </>
        ) : (
          <>
            <AlertTriangle className="h-3 w-3" />
            Outside radius
          </>
        )}
      </Badge>
    );
  };

  const getFlagBadgeVariant = (severity: string) => {
    switch (severity) {
      case 'error':
        return 'destructive';
      case 'warning':
        return 'secondary';
      case 'info':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  const getFlagIcon = (severity: string) => {
    switch (severity) {
      case 'error':
        return <AlertCircle className="h-3 w-3" />;
      case 'warning':
        return <AlertTriangle className="h-3 w-3" />;
      case 'info':
        return <Info className="h-3 w-3" />;
      default:
        return <AlertTriangle className="h-3 w-3" />;
    }
  };

  const getFlagTypeLabel = (flagType: string) => {
    return flagType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  if (isLoading || settingsLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Clock className="h-5 w-5" />
            <span>{t('timeAttendance.title', 'Time & Attendance')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2"></div>
              <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading...')}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Clock className="h-5 w-5" />
            <span>{t('timeAttendance.title', 'Time & Attendance')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <p className="text-sm text-destructive">
                {t('timeAttendance.error', 'Failed to load time tracking status')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const isLoggedIn = timeEntryData?.isLoggedIn || false;
  const currentEntry = timeEntryData?.entry;
  const isProcessing = mutations.clockIn.isPending || mutations.clockOut.isPending || isCapturing;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Clock className="h-5 w-5" />
            <span>{t('timeAttendance.title', 'Time & Attendance')}</span>
          </CardTitle>
          <CardDescription>
            {t('timeAttendance.description', 'Track your work hours with clock in and out')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status Display */}
          <div className="text-center py-4">
            {isLoggedIn && currentEntry ? (
              <div className="space-y-2">
                <div className="flex items-center justify-center space-x-2 text-green-600">
                  <ClockIcon className="h-5 w-5" />
                  <span className="font-medium">
                    {t('timeAttendance.clockedIn', 'You are currently clocked in')}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {t('timeAttendance.clockedInAt', 'Clocked in at')}: {formatDateTime(currentEntry.clock_in)}
                </p>
                <div className="flex flex-col items-center gap-2">
                  {currentEntry.ip_address_in && (
                    <p className="text-xs text-muted-foreground flex items-center space-x-1">
                      <MapPin className="h-3 w-3" />
                      <span>IP: {currentEntry.ip_address_in}</span>
                    </p>
                  )}
                  {geoEnabled && getGeoStatusBadge()}
                  {currentEntry.attendance_flags && currentEntry.attendance_flags.length > 0 && (
                    <div className="flex items-center gap-1 flex-wrap justify-center">
                      <TooltipProvider>
                        {currentEntry.attendance_flags.map((flag) => (
                          <Tooltip key={flag.id}>
                            <TooltipTrigger asChild>
                              <Badge 
                                variant={getFlagBadgeVariant(flag.flag_severity)}
                                className="gap-1 cursor-help"
                              >
                                {getFlagIcon(flag.flag_severity)}
                                {getFlagTypeLabel(flag.flag_type)}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="text-xs">{flag.flag_reason || 'Attendance discrepancy detected'}</p>
                            </TooltipContent>
                          </Tooltip>
                        ))}
                      </TooltipProvider>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-center space-x-2 text-muted-foreground">
                  <ClockIcon className="h-5 w-5" />
                  <span className="font-medium">
                    {t('timeAttendance.clockedOut', 'You are currently clocked out')}
                  </span>
                </div>
                
                {currentEntry?.clock_out ? (
                  <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {t('timeAttendance.lastSession', 'Last Session')}
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">{t('timeAttendance.in', 'In')}:</span>{' '}
                        <span className="font-medium">{formatTime(currentEntry.clock_in)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">{t('timeAttendance.out', 'Out')}:</span>{' '}
                        <span className="font-medium">{formatTime(currentEntry.clock_out)}</span>
                      </div>
                    </div>
                    <p className="text-sm">
                      <span className="text-muted-foreground">{t('timeAttendance.duration', 'Duration')}:</span>{' '}
                      <span className="font-medium">{formatDuration(currentEntry.clock_in, currentEntry.clock_out)}</span>
                    </p>
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      {currentEntry.ip_address_out && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          <span>IP: {currentEntry.ip_address_out}</span>
                        </p>
                      )}
                      {geoEnabled && currentEntry.geo_status && (
                        <Badge 
                          variant={currentEntry.geo_status === 'within_radius' ? 'default' : 'destructive'}
                          className="flex items-center gap-1"
                        >
                          {currentEntry.geo_status === 'within_radius' ? (
                            <><CheckCircle2 className="h-3 w-3" /> Within radius</>
                          ) : (
                            <><AlertTriangle className="h-3 w-3" /> Outside radius</>
                          )}
                        </Badge>
                      )}
                    </div>
                    {currentEntry.attendance_flags && currentEntry.attendance_flags.length > 0 && (
                      <div className="flex items-center gap-1 flex-wrap pt-1">
                        <TooltipProvider>
                          {currentEntry.attendance_flags.map((flag) => (
                            <Tooltip key={flag.id}>
                              <TooltipTrigger asChild>
                                <Badge 
                                  variant={getFlagBadgeVariant(flag.flag_severity)}
                                  className="gap-1 cursor-help"
                                >
                                  {getFlagIcon(flag.flag_severity)}
                                  {getFlagTypeLabel(flag.flag_type)}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs">{flag.flag_reason || 'Attendance discrepancy detected'}</p>
                              </TooltipContent>
                            </Tooltip>
                          ))}
                        </TooltipProvider>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {t('timeAttendance.readyToStart', 'Ready to start your work day?')}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Action Button */}
          <div className="flex justify-center">
            {isLoggedIn ? (
              <Button
                onClick={handleClockOut}
                disabled={isProcessing}
                variant="destructive"
                size="lg"
                className="px-8 py-4 text-lg font-semibold"
              >
                {isProcessing 
                  ? t('timeAttendance.clockingOut', 'Clocking out...') 
                  : t('timeAttendance.clockOut', 'Clock Out')
                }
              </Button>
            ) : (
              <Button
                onClick={handleClockIn}
                disabled={isProcessing}
                size="lg"
                className="px-8 py-4 text-lg font-semibold bg-green-600 hover:bg-green-700"
              >
                {isProcessing 
                  ? t('timeAttendance.clockingIn', 'Clocking in...') 
                  : t('timeAttendance.clockIn', 'Clock In')
                }
              </Button>
            )}
          </div>

          {/* Current Time Display */}
          <div className="text-center pt-2">
            <p className="text-sm text-muted-foreground">
              {t('timeAttendance.currentTime', 'Current time')}: {formatTime(new Date().toISOString())}
            </p>
          </div>
        </CardContent>
      </Card>

      <LocationConfirmationDialog
        open={showLocationConfirm}
        onConfirm={handleLocationConfirm}
        onCancel={() => {
          setShowLocationConfirm(false);
          setPendingAction(null);
        }}
        actionType={pendingAction || 'in'}
      />

      <LocationPermissionDialog
        open={showPermissionDialog}
        onRetry={handlePermissionRetry}
        onCancel={() => {
          setShowPermissionDialog(false);
          setPendingAction(null);
        }}
        isStrictMode={geoStrictMode}
      />

      <GeofenceErrorDialog
        open={!!geofenceError}
        onClose={() => {
          setGeofenceError(null);
          setPendingAction(null);
        }}
        onRetry={async () => {
          setGeofenceError(null);
          const location = await handleLocationCapture();
          if (location || !geoStrictMode) {
            await executeClockAction(location);
          }
        }}
        geoStatus={geofenceError?.geoStatus}
        message={geofenceError?.message}
        distance={geofenceError?.distance}
        allowedRadius={geofenceError?.allowedRadius}
        geofenceName={geofenceError?.locationName}
        geofenceAddress={geofenceError?.locationAddress}
      />
    </>
  );
};

export default TimeAttendance;
